import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  PageContainer,
  PageHeader,
  Button,
  Input,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Card,
  CardContent,
} from "@homy/ui";
import {
  Headphones,
  Search,
  RefreshCw,
  AlertTriangle,
  Clock,
  PauseCircle,
  CheckCircle2,
  Building2,
  User,
  ShieldAlert,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  XCircle,
} from "lucide-react";
import {
  listPlatformTickets,
  getPlatformSupportMetrics,
} from "../lib/api/supportClient";

// Priority Badge Component with distinct semantic styling
function PriorityBadge({ priority }) {
  const p = (priority || "MEDIUM").toUpperCase();
  switch (p) {
    case "URGENT":
      return (
        <Badge
          variant="outline"
          className="border-rose-500/40 bg-rose-500/15 text-rose-600 dark:text-rose-400 font-mono text-[11px] font-semibold gap-1"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
          URGENT
        </Badge>
      );
    case "HIGH":
      return (
        <Badge
          variant="outline"
          className="border-amber-500/40 bg-amber-500/15 text-amber-600 dark:text-amber-400 font-mono text-[11px] font-semibold"
        >
          HIGH
        </Badge>
      );
    case "MEDIUM":
      return (
        <Badge
          variant="outline"
          className="border-sky-500/40 bg-sky-500/15 text-sky-600 dark:text-sky-400 font-mono text-[11px]"
        >
          MEDIUM
        </Badge>
      );
    case "LOW":
    default:
      return (
        <Badge
          variant="outline"
          className="border-slate-500/30 bg-slate-500/10 text-muted-foreground font-mono text-[11px]"
        >
          LOW
        </Badge>
      );
  }
}

// Status Badge Component with SLA Paused and Active indicators
function TicketStatusBadge({ status }) {
  const s = (status || "NEW").toUpperCase();
  switch (s) {
    case "NEW":
      return (
        <Badge
          variant="outline"
          className="border-sky-500/30 bg-sky-500/10 text-sky-500 font-medium text-xs"
        >
          NEW
        </Badge>
      );
    case "OPEN":
      return (
        <Badge
          variant="outline"
          className="border-blue-500/30 bg-blue-500/10 text-blue-500 font-medium text-xs"
        >
          OPEN
        </Badge>
      );
    case "IN_PROGRESS":
      return (
        <Badge
          variant="outline"
          className="border-indigo-500/30 bg-indigo-500/10 text-indigo-400 font-medium text-xs"
        >
          IN PROGRESS
        </Badge>
      );
    case "WAITING":
    case "WAITING_CUSTOMER":
      return (
        <Badge
          variant="outline"
          className="border-amber-500/40 bg-amber-500/15 text-amber-400 font-medium text-xs gap-1"
        >
          <PauseCircle className="h-3 w-3 text-amber-400" />
          WAITING CUSTOMER
        </Badge>
      );
    case "RESOLVED":
      return (
        <Badge
          variant="outline"
          className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-medium text-xs gap-1"
        >
          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          RESOLVED
        </Badge>
      );
    case "CLOSED":
      return (
        <Badge
          variant="outline"
          className="border-slate-500/30 bg-slate-500/10 text-muted-foreground font-medium text-xs"
        >
          CLOSED
        </Badge>
      );
    default:
      return <Badge variant="outline">{s}</Badge>;
  }
}

// SLA Deadline Indicator Component
function SlaIndicator({ ticket }) {
  const isResolvedOrClosed = ["RESOLVED", "CLOSED"].includes(ticket.status);
  const isPaused = ticket.status === "WAITING_CUSTOMER";

  if (isResolvedOrClosed) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-400 font-mono">
        <CheckCircle2 className="h-3 w-3" /> Met
      </span>
    );
  }

  if (isPaused) {
    const pausedMinutes = ticket.totalPausedDurationMinutes || 0;
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-400 font-mono" title="SLA Clock is paused while awaiting customer response">
        <PauseCircle className="h-3 w-3" /> Paused ({pausedMinutes}m)
      </span>
    );
  }

  if (!ticket.slaExpiresAt) {
    return <span className="text-xs text-muted-foreground font-mono">—</span>;
  }

  const expiresAt = new Date(ticket.slaExpiresAt);
  const now = new Date();
  const diffMs = expiresAt - now;
  const isBreached = diffMs < 0;

  if (isBreached) {
    const overdueHours = Math.abs(Math.round(diffMs / (1000 * 60 * 60)));
    return (
      <Badge
        variant="outline"
        className="border-rose-500/40 bg-rose-500/15 text-rose-400 font-mono text-[11px] gap-1"
      >
        <AlertTriangle className="h-3 w-3 text-rose-400" />
        BREACHED (+{overdueHours}h)
      </Badge>
    );
  }

  const hoursRemaining = Math.floor(diffMs / (1000 * 60 * 60));
  const minutesRemaining = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  let colorClass = "text-emerald-400";
  if (hoursRemaining < 1) {
    colorClass = "text-rose-400 animate-pulse";
  } else if (hoursRemaining < 4) {
    colorClass = "text-amber-400";
  }

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono font-medium ${colorClass}`}>
      <Clock className="h-3 w-3" />
      {hoursRemaining}h {minutesRemaining}m
    </span>
  );
}

export function PlatformSupportPage() {
  const navigate = useNavigate();

  // Filters state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [orgFilter, setOrgFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("ALL");
  const [slaBreachedOnly, setSlaBreachedOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Query: Platform Support Metrics
  const {
    data: metrics,
    isLoading: metricsLoading,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ["platform-support-metrics"],
    queryFn: getPlatformSupportMetrics,
    refetchInterval: 30000,
  });

  // Query: Platform Support Tickets List
  const {
    data: ticketsData,
    isLoading: ticketsLoading,
    isFetching,
    refetch: refetchTickets,
  } = useQuery({
    queryKey: [
      "platform-support-tickets",
      searchTerm,
      statusFilter,
      priorityFilter,
      categoryFilter,
      orgFilter,
      assignedFilter,
      slaBreachedOnly,
      currentPage,
    ],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (priorityFilter !== "ALL") params.priority = priorityFilter;
      if (categoryFilter !== "ALL") params.category = categoryFilter;
      if (orgFilter.trim()) params.submittingOrganizationId = orgFilter.trim();
      if (assignedFilter !== "ALL") params.assignedTo = assignedFilter;
      if (slaBreachedOnly) params.isSlaBreached = true;

      return await listPlatformTickets(params);
    },
    refetchInterval: 30000,
  });

  const handleRefreshAll = () => {
    refetchMetrics();
    refetchTickets();
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
    setPriorityFilter("ALL");
    setCategoryFilter("ALL");
    setOrgFilter("");
    setAssignedFilter("ALL");
    setSlaBreachedOnly(false);
    setCurrentPage(1);
  };

  const tickets = ticketsData?.items || [];
  const totalTickets = ticketsData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalTickets / pageSize));

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <PageHeader
        title="Platform Support Console"
        description="Sovereign command center for Track 2 platform tickets (PLT-YYYY-NNNN). Manage technical incidents, platform inquiries, and unassigned public quarantine."
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleRefreshAll}
            disabled={isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* KPI Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card
          className={`cursor-pointer transition-all border-border hover:border-primary/50 ${
            statusFilter === "ALL" && !slaBreachedOnly ? "ring-1 ring-primary/40" : ""
          }`}
          onClick={() => {
            setStatusFilter("ALL");
            setSlaBreachedOnly(false);
          }}
        >
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold">
              ACTIVE TICKETS
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {metricsLoading ? "..." : metrics?.totalOpen ?? 0}
            </p>
            <p className="text-[11px] text-muted-foreground">Open or in progress</p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all border-border hover:border-indigo-500/50 ${
            statusFilter === "IN_PROGRESS" ? "ring-1 ring-indigo-500/40" : ""
          }`}
          onClick={() => {
            setStatusFilter("IN_PROGRESS");
            setSlaBreachedOnly(false);
          }}
        >
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-indigo-400 font-semibold">
              IN PROGRESS
            </p>
            <p className="text-2xl font-bold font-mono text-indigo-300">
              {metricsLoading
                ? "..."
                : (metrics?.inProgressCount ?? (statusFilter === "IN_PROGRESS" ? totalTickets : tickets.filter((t) => t.status === "IN_PROGRESS").length))}
            </p>
            <p className="text-[11px] text-muted-foreground">Under operator review</p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all border-border hover:border-amber-500/50 ${
            statusFilter === "WAITING_CUSTOMER" || statusFilter === "WAITING" ? "ring-1 ring-amber-500/40" : ""
          }`}
          onClick={() => {
            setStatusFilter("WAITING_CUSTOMER");
            setSlaBreachedOnly(false);
          }}
        >
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-amber-400 font-semibold flex items-center gap-1">
              <PauseCircle className="h-3 w-3" /> WAITING CUSTOMER
            </p>
            <p className="text-2xl font-bold font-mono text-amber-300">
              {metricsLoading
                ? "..."
                : (metrics?.waitingCustomerCount ?? (["WAITING_CUSTOMER", "WAITING"].includes(statusFilter) ? totalTickets : tickets.filter((t) => ["WAITING_CUSTOMER", "WAITING"].includes(t.status)).length))}
            </p>
            <p className="text-[11px] text-muted-foreground">SLA clock paused</p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all border-border hover:border-rose-500/50 ${
            slaBreachedOnly ? "ring-1 ring-rose-500/40 bg-rose-500/5" : ""
          }`}
          onClick={() => {
            setSlaBreachedOnly((prev) => !prev);
            setStatusFilter("ALL");
          }}
        >
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-rose-400 font-semibold flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> SLA BREACHED
            </p>
            <p className="text-2xl font-bold font-mono text-rose-400">
              {metricsLoading ? "..." : metrics?.slaBreachedCount ?? 0}
            </p>
            <p className="text-[11px] text-muted-foreground">Exceeded SLA target</p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all border-border hover:border-rose-500/50 ${
            priorityFilter === "URGENT" ? "ring-1 ring-rose-500/40" : ""
          }`}
          onClick={() => {
            setPriorityFilter(priorityFilter === "URGENT" ? "ALL" : "URGENT");
          }}
        >
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-rose-300 font-semibold flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> URGENT / HIGH
            </p>
            <p className="text-2xl font-bold font-mono text-rose-300">
              {metricsLoading ? "..." : metrics?.urgentCount ?? 0}
            </p>
            <p className="text-[11px] text-muted-foreground">Priority escalation</p>
          </CardContent>
        </Card>
      </div>

      {/* Multi-Facet Filter Toolbar */}
      <div className="bg-card border border-border p-4 rounded-xl space-y-3">
        <div className="flex flex-col md:flex-row items-center gap-3">
          {/* Search Input */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by ticket (PLT-...), subject, submitter email, or name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 bg-input"
            />
          </div>

          {/* Submitting Org ID Filter */}
          <div className="w-full md:w-56">
            <Input
              placeholder="Filter by Org ID..."
              value={orgFilter}
              onChange={(e) => {
                setOrgFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="bg-input font-mono text-xs"
            />
          </div>

          {/* Reset button */}
          {(searchTerm ||
            statusFilter !== "ALL" ||
            priorityFilter !== "ALL" ||
            categoryFilter !== "ALL" ||
            orgFilter ||
            assignedFilter !== "ALL" ||
            slaBreachedOnly) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        {/* Dropdown Filters Row */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/50 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Statuses</option>
              <option value="NEW">NEW</option>
              <option value="OPEN">OPEN</option>
              <option value="IN_PROGRESS">IN PROGRESS</option>
              <option value="WAITING">WAITING</option>
              <option value="WAITING_CUSTOMER">WAITING CUSTOMER</option>
              <option value="RESOLVED">RESOLVED</option>
              <option value="CLOSED">CLOSED</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Priority:</span>
            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Priorities</option>
              <option value="URGENT">URGENT</option>
              <option value="HIGH">HIGH</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="LOW">LOW</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Category:</span>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Categories</option>
              <option value="TECHNICAL">TECHNICAL</option>
              <option value="BILLING">BILLING</option>
              <option value="GENERAL">GENERAL</option>
              <option value="MAINTENANCE">MAINTENANCE</option>
              <option value="LEASE">LEASE</option>
              <option value="COMPLAINT">COMPLAINT</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Assignee:</span>
            <select
              value={assignedFilter}
              onChange={(e) => {
                setAssignedFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Assignees</option>
              <option value="me">Assigned to Me</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5 ml-auto">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={slaBreachedOnly}
                onChange={(e) => {
                  setSlaBreachedOnly(e.target.checked);
                  setCurrentPage(1);
                }}
                className="rounded border-border text-rose-500 focus:ring-rose-500"
              />
              <span className={slaBreachedOnly ? "text-rose-400 font-semibold" : ""}>
                Only Breached SLA
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        {ticketsLoading ? (
          <div className="p-16 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
            <RefreshCw className="h-6 w-6 animate-spin text-primary" />
            <p>Loading sovereign platform support tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-16 text-center text-sm text-muted-foreground space-y-2">
            <Headphones className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="font-medium text-foreground">No platform tickets found</p>
            <p className="text-xs text-muted-foreground">
              Try adjusting your search criteria, clearing filters, or checking other categories.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Ticket #</TableHead>
                <TableHead className="w-[220px]">Submitter / Organization</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead className="w-[110px]">Priority</TableHead>
                <TableHead className="w-[150px]">Status</TableHead>
                <TableHead className="w-[140px]">SLA Target</TableHead>
                <TableHead className="w-[120px]">Created</TableHead>
                <TableHead className="w-[80px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((ticket) => {
                const isOrgAffiliated = Boolean(ticket.organizationId);
                return (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => navigate(`/support/${ticket.id}`)}
                  >
                    {/* Ticket Reference */}
                    <TableCell className="font-mono text-xs font-semibold text-primary">
                      {ticket.caseNumber || ticket.referenceNumber || ticket.id}
                    </TableCell>

                    {/* Submitter / Organization */}
                    <TableCell>
                      {isOrgAffiliated ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate max-w-[170px]" title={ticket.organizationId}>
                              Org: {ticket.organizationId.slice(0, 8)}...
                            </span>
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate max-w-[170px]">
                            {ticket.guestEmail || ticket.userId || "Staff Submitter"}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
                            <User className="h-3 w-3" /> Public Guest
                          </span>
                          <p className="text-[11px] text-muted-foreground truncate max-w-[170px]">
                            {ticket.guestEmail || ticket.guestName || "Unassigned Quarantine"}
                          </p>
                        </div>
                      )}
                    </TableCell>

                    {/* Subject & Category */}
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium text-sm text-foreground leading-tight line-clamp-1">
                          {ticket.subject || ticket.description?.slice(0, 60) || "No Subject"}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px] uppercase font-mono px-1.5 py-0">
                            {ticket.category || "GENERAL"}
                          </Badge>
                          <span className="text-[11px] text-muted-foreground font-mono">
                            via {ticket.channel || "WEB"}
                          </span>
                        </div>
                      </div>
                    </TableCell>

                    {/* Priority */}
                    <TableCell>
                      <PriorityBadge priority={ticket.priority} />
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <TicketStatusBadge status={ticket.status} />
                    </TableCell>

                    {/* SLA Indicator */}
                    <TableCell>
                      <SlaIndicator ticket={ticket} />
                    </TableCell>

                    {/* Created Date */}
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {ticket.createdAt
                        ? new Date(ticket.createdAt).toLocaleDateString()
                        : "—"}
                    </TableCell>

                    {/* Action */}
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        title="Open Ticket Details"
                        onClick={() => navigate(`/support/${ticket.id}`)}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* Pagination Footer */}
        {totalTickets > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/10 text-xs text-muted-foreground">
            <div>
              Showing <span className="font-medium text-foreground">{tickets.length}</span> of{" "}
              <span className="font-medium text-foreground">{totalTickets}</span> tickets
            </div>
            <div className="flex items-center gap-2">
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={currentPage <= 1 || isFetching}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={currentPage >= totalPages || isFetching}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export default PlatformSupportPage;
